import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BlogPost } from '../types';
import BlogCard from './BlogCard';
import BlogModal from './BlogModal';
import { getBlogPostsData } from '../data/blog';

const BlogPage: React.FC = () => {
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const postsPerPage = 6;

  useEffect(() => {
    const loadBlogPosts = async () => {
      try {
        setLoading(true);
        const posts = await getBlogPostsData();
        const sortedPosts = posts.sort((a, b) => {
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        });
        setBlogPosts(sortedPosts);
      } catch (error) {
        console.error('Error loading blog posts:', error);
        setBlogPosts([]);
      } finally {
        setLoading(false);
      }
    };

    loadBlogPosts();
  }, []);

  const openPost = (post: BlogPost) => {
    setSelectedPost(post);
  };

  const closePost = () => {
    setSelectedPost(null);
  };

  // Pagination Logic
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = blogPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(blogPosts.length / postsPerPage);

  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generate page numbers for pagination
  const pageNumbers: number[] = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <>
      <div className="blog-page">
        <div className="container">
          {/* Blog Header */}
          <div className="blog-header">
            <Link to="/" className="back-link">
              ← Back to Portfolio
            </Link>
            <h1 className="blog-page-title">
              <span className="gradient-text">Blog Posts</span>
            </h1>
            <p className="blog-page-subtitle">
              Thoughts on development, design, and technology
            </p>
          </div>

          {/* Blog Posts Grid */}
          {loading ? (
            <div className="loading-indicator">
              <p>Loading blog posts...</p>
            </div>
          ) : blogPosts.length > 0 ? (
            <>
              <div className="blog-grid">
                {currentPosts.map((post) => (
                  <BlogCard key={post.id} post={post} onOpen={openPost} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    Previous
                  </button>

                  <div className="page-numbers">
                    {pageNumbers.map(number => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`page-number ${currentPage === number ? 'active' : ''}`}
                      >
                        {number}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}

              {/* Posts Summary */}
              <div className="posts-summary">
                <p>
                  Showing {indexOfFirstPost + 1}-{Math.min(indexOfLastPost, blogPosts.length)} of {blogPosts.length} posts
                </p>
              </div>

              {/* Subscribe CTA */}
              <div className="subscribe-cta">
                <p className="subscribe-cta-lead">
                  Subscribe to get new posts in your inbox.
                </p>
                <iframe
                  src="https://sohailmo.substack.com/embed?transparent=1&light=1"
                  width="480"
                  height="320"
                  style={{ border: 0, background: 'transparent' }}
                  frameBorder="0"
                  scrolling="no"
                  loading="lazy"
                  title="Subscribe to Sohail's Substack"
                />
              </div>
            </>
          ) : (
            <div className="no-posts-message">
              <p>No blog posts available at the moment.</p>
            </div>
          )}
        </div>
      </div>

      {/* Blog Modal */}
      {selectedPost && (
        <BlogModal post={selectedPost} onClose={closePost} />
      )}
    </>
  );
};

export default BlogPage;
